import {debounce} from '@github/mini-throttle/decorators'
import {COPILOT_AUTH_TOKEN_KEY} from '@github-ui/copilot-auth-token'
import safeStorage from '@github-ui/safe-storage'
import {useCallback, useState} from 'react'

import {referenceID} from './copilot-chat-helpers'
import type {
  CopilotChatModel,
  CopilotChatReference,
  CopilotChatRepo,
  CopilotChatSettings,
  CopilotSpaceTab,
  FileDiffReference,
  IndexCopilotSpace,
  MediaContentItem,
} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import type {SkuType} from './sku-types'

interface MessageRequestID {
  messageID: string
  requestID: string
}

type EntrypointMessage = {
  message: string
  references?: CopilotChatReference[]
  model?: CopilotChatModel
  mediaContent?: MediaContentItem[]
  branch?: string
}

export class CopilotLocalStorage {
  private COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY = 'COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY'
  private COPILOT_SELECTED_THREAD_ID_STORAGE_KEY = 'COPILOT_SELECTED_THREAD_ID_STORAGE_KEY'
  private COPILOT_COLLAPSED_STATE_KEY = 'COPILOT_COLLAPSED_STATE_KEY'
  private COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY = 'COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY'
  private COPILOT_PANEL_HEIGHT = 'COPILOT_PANEL_HEIGHT'
  private DEFAULT_PANEL_HEIGHT = 600
  private COPILOT_PANEL_WIDTH = 'COPILOT_PANEL_WIDTH'
  private COPILOT_ENTRYPOINT_MESSAGE_KEY = (threadID: string | undefined | null) =>
    threadID ? `COPILOT_ENTRYPOINT_MESSAGE_KEY_${threadID}` : 'COPILOT_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY = 'COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY'
  private COPILOT_MESSAGE_REQUEST_ID_KEY = 'COPILOT_MESSAGE_REQUEST_ID_KEY'
  private COPILOT_METER_BANNER_FLAG = 'COPILOT_METER_BANNER_FLAG'
  private COPILOT_QUOTA_EXCEEDED_BANNER_FLAG = 'COPILOT_QUOTA_EXCEEDED_BANNER_FLAG'
  private COPILOT_CODING_AGENT_QUOTA_BANNER_DISMISSED_PREFIX = 'COPILOT_CODING_AGENT_QUOTA_BANNER_DISMISSED_'
  private COPILOT_ANIMATION_FLAG = 'COPILOT_ANIMATION_FLAG'

  private COPILOT_WRAP_CODE_LINES_KEY = 'COPILOT_WRAP_CODE_LINES_KEY'
  private COPILOT_TRACING_REQUESTED_FLAG = 'COPILOT_TRACING_REQUESTED_FLAG'
  private COPILOT_DIFF_REFERENCES_KEY = 'COPILOT_DIFF_REFERENCES_KEY'
  private COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT = 'COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT'
  private COPILOT_SPACES_SELECTED_TAB = 'COPILOT_SPACES_SELECTED_TAB'
  private COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE = 'COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE'
  private COPILOT_LAST_USED_REPOSITORY_KEY = 'COPILOT_LAST_USED_REPOSITORY_KEY'
  private COPILOT_ACTIVE_MODE_KEY = 'COPILOT_ACTIVE_MODE_KEY'
  private COPILOT_SELECTED_MODEL_VERSION_KEY = 'COPILOT_SELECTED_MODEL_VERSION'
  private COPILOT_LANDED_ON_AGENTS_VIEW_KEY = 'COPILOT_LANDED_ON_AGENTS_VIEW_KEY'
  private lastCheckedModelVersion: string | null = null

  private MAX_MESSAGE_REQUEST_ID_STORAGE_COUNT = 100
  DEFAULT_PANEL_WIDTH = 480

  // Version for model selection storage. Increment this in code to trigger future clearing.
  private CURRENT_MODEL_STORAGE_VERSION = '8'

  private COPILOT_SELECTED_MODEL_STORAGE_KEY = (threadID: string | null) => `COPILOT_SELECTED_MODEL_${threadID}`

  private COPILOT_SETTINGS_STORAGE_KEY = (threadID: string | null) => `COPILOT_SETTINGS_${threadID}`

  private COPILOT_SAVED_USER_MESSAGE_KEY = (threadID: string | null): string => `COPILOT_SAVED_USER_MESSAGE_${threadID}`

  private COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY = (threadID: string | null): string =>
    `COPILOT_SAVED_USER_MESSAGE_ON_ERROR_${threadID}`

  private COPILOT_CURRENT_REFERENCES_KEY = (threadID: string | null): string => `COPILOT_CURRENT_REFERENCES_${threadID}`

  private COPILOT_SELECTED_TOPIC_STORAGE_KEY = (threadId: string): string => `COPILOT_SELECTED_TOPIC_${threadId}`

  private localStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24, // 1 day
  })

  private mediumStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24 * 28, // 28 days - for model selection persistence
  })

  private longStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
  })

  private tempStorage = safeStorage('localStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 30, // 30 minutes
  })

  private sessionStorage = safeStorage('sessionStorage', {
    throwQuotaErrorsOnSet: false,
    ttl: 1000 * 60 * 60 * 24, // 1 day
  })

  get selectedThreadID(): string | null {
    return this.localStorage.getItem(this.COPILOT_SELECTED_THREAD_ID_STORAGE_KEY)
  }

  set selectedThreadID(threadID: string | null) {
    const key = this.COPILOT_SELECTED_THREAD_ID_STORAGE_KEY
    if (threadID == null) {
      this.localStorage.removeItem(key)
      return
    }
    this.localStorage.setItem(key, threadID)
  }

  getModel(threadID: string | null): CopilotChatModel | null {
    // Auto-clear model selection for new threads if version changed (when FF is enabled)
    if (this.lastCheckedModelVersion !== this.CURRENT_MODEL_STORAGE_VERSION && threadID === null) {
      this.checkAndClearModelSelection()
    }

    const json = this.mediumStorage.getItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(threadID))
    return json === null ? json : (JSON.parse(json) as CopilotChatModel)
  }

  setModel(threadID: string | null, value: CopilotChatModel | null, updateDefault = true) {
    this.mediumStorage.setItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(threadID), JSON.stringify(value))
    // If in a thread and updateDefault is true, also update the model for new conversations.
    // This ensures the default model persists when user explicitly changes it, but not when simply browsing old conversations.
    if (threadID && updateDefault) {
      this.mediumStorage.setItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(null), JSON.stringify(value))
    }
  }

  /**
   * Clears cached model selection for new threads when version changes.
   * Only runs when clearModelSelectionForDefaultChange FF is enabled.
   * Only clears null thread to allow existing threads to keep their selections.
   */
  private checkAndClearModelSelection(): void {
    // Update the last checked version to prevent redundant checks
    this.lastCheckedModelVersion = this.CURRENT_MODEL_STORAGE_VERSION

    // Only proceed if feature flag is enabled
    if (!copilotFeatureFlags.clearModelSelectionForDefaultChange) {
      return
    }

    const storedVersion = this.longStorage.getItem(this.COPILOT_SELECTED_MODEL_VERSION_KEY)

    if (storedVersion !== this.CURRENT_MODEL_STORAGE_VERSION) {
      this.mediumStorage.removeItem(this.COPILOT_SELECTED_MODEL_STORAGE_KEY(null))

      // Update version to prevent clearing again
      this.longStorage.setItem(this.COPILOT_SELECTED_MODEL_VERSION_KEY, this.CURRENT_MODEL_STORAGE_VERSION)
    }
  }

  get settings(): CopilotChatSettings | null {
    const json = this.localStorage.getItem(this.COPILOT_SETTINGS_STORAGE_KEY(this.selectedThreadID))
    return json === null ? json : (JSON.parse(json) as CopilotChatSettings)
  }

  set settings(value: CopilotChatSettings) {
    this.localStorage.setItem(this.COPILOT_SETTINGS_STORAGE_KEY(this.selectedThreadID), JSON.stringify(value))
  }

  getPanelHeight(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_HEIGHT)
    return val ? parseInt(val) : this.DEFAULT_PANEL_HEIGHT
  }

  setPanelHeight(height: number) {
    const key = this.COPILOT_PANEL_HEIGHT
    this.localStorage.setItem(key, height.toString())
  }

  getPanelWidth(): number {
    const val = this.localStorage.getItem(this.COPILOT_PANEL_WIDTH)
    return val ? parseInt(val) : this.DEFAULT_PANEL_WIDTH
  }

  setPanelWidth(width: number) {
    const key = this.COPILOT_PANEL_WIDTH
    this.localStorage.setItem(key, width.toString())
  }

  getRepoCustomInstructionsState(): boolean {
    const val = this.localStorage.getItem(this.COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY)
    // NOTE: repo custom instructions are enabled by default
    return val !== 'false'
  }

  setRepoCustomInstructionsState(enabled: boolean) {
    const key = this.COPILOT_REPO_CUSTOM_INSTRUCTIONS_STATE_KEY
    this.localStorage.setItem(key, enabled.toString())
  }

  getCollapsedState(): boolean {
    const val = this.sessionStorage.getItem(this.COPILOT_COLLAPSED_STATE_KEY)
    return val !== 'false'
  }

  setCollapsedState(collapsed: boolean) {
    const key = this.COPILOT_COLLAPSED_STATE_KEY
    this.sessionStorage.setItem(key, collapsed.toString())
  }

  getSelectedTopic(threadID: string): string | null {
    return this.localStorage.getItem(this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID))
  }

  setSelectedTopic(threadID: string, topic: string | null) {
    if (topic == null) {
      this.localStorage.removeItem(this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID))
      return
    }

    const key = this.COPILOT_SELECTED_TOPIC_STORAGE_KEY(threadID)
    this.localStorage.setItem(key, topic)
  }

  getSavedMessage(threadID: string | null): string | null {
    return this.localStorage.getItem(this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID))
  }

  @debounce(100)
  setSavedMessage(threadID: string | null, message: string | null) {
    this.setSavedMessageFast(threadID, message)
  }

  // Same as setSavedMessage but without debounce
  // Used when we want to set/reset the message immediately (e.g. when sending user meessage to a new thread)
  setSavedMessageFast(threadID: string | null, message: string | null) {
    const key = this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID)

    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, message)
  }

  clearSavedUserMessage(threadID: string | null) {
    this.localStorage.removeItem(this.COPILOT_SAVED_USER_MESSAGE_KEY(threadID))
  }

  getSavedUserMessageOnError(threadID: string | null): string | null {
    return this.localStorage.getItem(this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID))
  }

  setSavedUserMessageOnError(threadID: string | null, message: string | null) {
    const key = this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID)

    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, message)
  }

  clearSavedUserMessageOnError(threadID: string | null) {
    this.localStorage.removeItem(this.COPILOT_SAVED_USER_MESSAGE_ON_ERROR_KEY(threadID))
  }

  getEntrypointMessage(threadID?: string | null) {
    return this.getEntrypointMessageImpl(this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID))
  }

  private getEntrypointMessageImpl(key: string): {
    message: string
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    branch?: string
  } | null {
    const entrypointMessage = this.localStorage.getItem(key)
    if (entrypointMessage == null) {
      return null
    }
    let parsedMessage: EntrypointMessage | undefined
    try {
      parsedMessage = JSON.parse(entrypointMessage)
    } catch {
      return null
    }
    if (!parsedMessage || !parsedMessage.message) {
      return null
    }
    return {
      message: parsedMessage.message,
      references: parsedMessage.references,
      model: parsedMessage.model,
      mediaContent: parsedMessage.mediaContent,
      branch: parsedMessage.branch,
    }
  }

  clearEntrypointMessage(threadID?: string | null): void {
    this.localStorage.removeItem(this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID))
  }

  setEntrypointMessage({
    message,
    references,
    model,
    mediaContent,
    threadID,
  }: {
    message: string | null
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    threadID?: string | null
  }): void {
    this.setEntrypointMessageImpl({
      message,
      references,
      model,
      mediaContent,
      key: this.COPILOT_ENTRYPOINT_MESSAGE_KEY(threadID),
    })
  }

  private setEntrypointMessageImpl({
    message,
    references,
    model,
    mediaContent,
    branch,
    key,
  }: {
    message: string | null
    references?: CopilotChatReference[]
    model?: CopilotChatModel
    mediaContent?: MediaContentItem[]
    branch?: string
    key: string
  }): void {
    if (message == null) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, JSON.stringify({message, references, model, mediaContent, branch}))
  }

  getTaskEntrypointMessage(): {message: string; references?: CopilotChatReference[]; branch?: string} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY)
  }

  setTaskEntrypointMessage(message: string | null, references?: CopilotChatReference[], branch?: string): void {
    this.setEntrypointMessageImpl({message, references, branch, key: this.COPILOT_TASK_ENTRYPOINT_MESSAGE_KEY})
  }

  getLoopEntrypointMessage(): {message: string; references?: CopilotChatReference[]} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY)
  }

  setLoopEntrypointMessage(message: string | null, references?: CopilotChatReference[]): void {
    this.setEntrypointMessageImpl({message, references, key: this.COPILOT_LOOP_ENTRYPOINT_MESSAGE_KEY})
  }

  getSparkEntrypointMessage(): {message: string; references?: CopilotChatReference[]} | null {
    return this.getEntrypointMessageImpl(this.COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY)
  }

  setSparkEntrypointMessage(message: string | null): void {
    this.setEntrypointMessageImpl({message, key: this.COPILOT_SPARK_ENTRYPOINT_MESSAGE_KEY})
  }

  getLastUsedRepository(): CopilotChatRepo | null {
    const val = this.localStorage.getItem(this.COPILOT_LAST_USED_REPOSITORY_KEY)
    if (!val) return null

    try {
      return JSON.parse(val) as CopilotChatRepo
    } catch {
      return null
    }
  }

  setLastUsedRepository(reference: CopilotChatRepo): void {
    this.localStorage.setItem(this.COPILOT_LAST_USED_REPOSITORY_KEY, JSON.stringify(reference))
  }

  getCurrentReferences(threadID: string | null): CopilotChatReference[] | null {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)

    const val = this.localStorage.getItem(key)
    if (!val) return null

    return JSON.parse(val) as CopilotChatReference[]
  }

  setCurrentReferences(threadID: string | null, references: CopilotChatReference[]): void {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)
    const uniqueReferences = references.filter((ref, index) => {
      return index === references.findIndex(r => referenceID(ref) === referenceID(r))
    })
    this.localStorage.setItem(key, JSON.stringify(uniqueReferences))
  }

  clearCurrentReferences(threadID: string | null): void {
    const key = this.COPILOT_CURRENT_REFERENCES_KEY(threadID)
    this.localStorage.removeItem(key)
  }

  migrateNullThreadToNewThread(newThreadID: string): void {
    const savedMessage = this.getSavedMessage(null)
    this.setSavedMessageFast(null, null)
    if (savedMessage) this.setSavedMessageFast(newThreadID, savedMessage)

    const savedRefs = this.getCurrentReferences(null)
    this.clearCurrentReferences(null)
    if (savedRefs) this.setCurrentReferences(newThreadID, savedRefs)

    const nullThreadSettings = this.settings
    if (nullThreadSettings) {
      this.localStorage.setItem(this.COPILOT_SETTINGS_STORAGE_KEY(newThreadID), JSON.stringify(nullThreadSettings))
    }
    this.selectedThreadID = newThreadID
    this.localStorage.removeItem(this.COPILOT_SETTINGS_STORAGE_KEY(null))
  }

  getImmersiveSidebarCollapsedState(): boolean {
    const item = this.localStorage.getItem(this.COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY)
    return item === null || item === 'true'
  }

  setImmersiveSidebarCollapsedState(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_IMMERSIVE_SIDEBAR_COLLAPSED_STATE_KEY, String(state))
  }

  getCopilotDiffReferences(): FileDiffReference[] | null {
    const val = this.localStorage.getItem(this.COPILOT_DIFF_REFERENCES_KEY)
    if (!val) return null

    return JSON.parse(val) as FileDiffReference[]
  }

  setCopilotDiffReferences(fileDiffReferences: FileDiffReference | FileDiffReference[]): void {
    const referencesArray = Array.isArray(fileDiffReferences) ? fileDiffReferences : [fileDiffReferences]
    this.localStorage.setItem(this.COPILOT_DIFF_REFERENCES_KEY, JSON.stringify(referencesArray))
  }

  removeCopilotDiffReferences(): void {
    this.localStorage.removeItem(this.COPILOT_DIFF_REFERENCES_KEY)
  }

  clearAuthToken(): void {
    this.localStorage.removeItem(COPILOT_AUTH_TOKEN_KEY)
  }

  getMeterBannerFlag(): boolean | null {
    const value = this.localStorage.getItem(this.COPILOT_METER_BANNER_FLAG)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  setMeterBannerFlag(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_METER_BANNER_FLAG, String(state))
  }

  removeMeterBannerFlag(): void {
    this.localStorage.removeItem(this.COPILOT_METER_BANNER_FLAG)
  }

  getQuotaExceededBannerFlag(): boolean | null {
    const value = this.tempStorage.getItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  setQuotaExceededBannerFlag(state: boolean): void {
    this.tempStorage.setItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG, String(state))
  }

  removeQuotaExceededBannerFlag(): void {
    this.tempStorage.removeItem(this.COPILOT_QUOTA_EXCEEDED_BANNER_FLAG)
  }

  /**
   * Get the dismissed state for the Copilot Coding Agent quota banner.
   * Uses reset date in key so it auto-expires when a new billing cycle starts.
   */
  getCodingAgentQuotaBannerDismissed(resetDate: string): boolean {
    if (!resetDate) return false
    const key = `${this.COPILOT_CODING_AGENT_QUOTA_BANNER_DISMISSED_PREFIX}${resetDate}`
    const value = this.localStorage.getItem(key)
    return value === 'true'
  }

  /**
   * Set the dismissed state for the Copilot Coding Agent quota banner.
   * Uses reset date in key so it auto-expires when a new billing cycle starts.
   */
  setCodingAgentQuotaBannerDismissed(resetDate: string): void {
    if (!resetDate) return
    const key = `${this.COPILOT_CODING_AGENT_QUOTA_BANNER_DISMISSED_PREFIX}${resetDate}`
    this.localStorage.setItem(key, 'true')
  }

  getSkuSuccessBannerFlag(skuType: SkuType): boolean | null {
    return this.getBooleanFlag(this.skuSuccessBannerKey(skuType))
  }

  setSkuSuccessBannerFlag(skuType: SkuType, state: boolean): void {
    this.tempStorage.setItem(this.skuSuccessBannerKey(skuType), String(state))
  }

  removeSkuSuccessBannerFlag(skuType: SkuType): void {
    this.tempStorage.removeItem(this.skuSuccessBannerKey(skuType))
  }

  getAnimationFlag(): boolean | null {
    return this.getBooleanFlag(this.COPILOT_ANIMATION_FLAG)
  }

  setAnimationFlag(state: boolean): void {
    this.tempStorage.setItem(this.COPILOT_ANIMATION_FLAG, String(state))
  }

  removeAnimationFlag(): void {
    this.tempStorage.removeItem(this.COPILOT_ANIMATION_FLAG)
  }

  getTracingRequestedFlag(): boolean {
    return this.localStorage.getItem(this.COPILOT_TRACING_REQUESTED_FLAG) === 'true'
  }

  setTracingRequestedFlag(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_TRACING_REQUESTED_FLAG, String(state))
  }

  getWrapCodeLines(): boolean {
    const item = this.localStorage.getItem(this.COPILOT_WRAP_CODE_LINES_KEY)
    return item === 'true'
  }

  setWrapCodeLines(state: boolean): void {
    this.localStorage.setItem(this.COPILOT_WRAP_CODE_LINES_KEY, String(state))
  }

  getBooleanFlag(key: string, storage = this.tempStorage): boolean | null {
    const value = storage.getItem(key)
    if (value === null) {
      return null
    }
    return value === 'true'
  }

  getSpacesMcpBannerDismissalCount(): number {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT)
    return val ? parseInt(val, 10) : 0
  }

  setSpacesMcpBannerDismissalCount(count: number) {
    const key = this.COPILOT_SPACES_MCP_BANNER_DISMISSAL_COUNT
    this.localStorage.setItem(key, count.toString())
  }

  getSpacesSelectedTab(): CopilotSpaceTab {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_SELECTED_TAB)
    return (val as CopilotSpaceTab) || 'personal'
  }

  setSpacesSelectedTab(tab: CopilotSpaceTab) {
    const key = this.COPILOT_SPACES_SELECTED_TAB
    this.localStorage.setItem(key, tab)
  }

  getLastSelectedSpace(): IndexCopilotSpace | undefined {
    const val = this.localStorage.getItem(this.COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE)
    if (!val) return undefined

    try {
      return JSON.parse(val) as IndexCopilotSpace
    } catch {
      return undefined
    }
  }

  setLastSelectedSpace(space: IndexCopilotSpace | undefined): void {
    const key = this.COPILOT_SPACES_SELECTION_MENU_LAST_SELECTED_SPACE
    if (space === undefined) {
      this.localStorage.removeItem(key)
      return
    }

    this.localStorage.setItem(key, JSON.stringify(space))
  }

  getActiveMode(): string | null {
    return this.longStorage.getItem(this.COPILOT_ACTIVE_MODE_KEY)
  }

  setActiveMode(modeName: string): void {
    this.longStorage.setItem(this.COPILOT_ACTIVE_MODE_KEY, modeName)
  }

  getLandedOnAgentsView(): boolean {
    return this.sessionStorage.getItem(this.COPILOT_LANDED_ON_AGENTS_VIEW_KEY) === 'true'
  }

  setLandedOnAgentsView(state: boolean): void {
    this.sessionStorage.setItem(this.COPILOT_LANDED_ON_AGENTS_VIEW_KEY, String(state))
  }

  // Get the success banner storage key for a Copilot SKU
  private skuSuccessBannerKey(skuType: SkuType): string {
    return `COPILOT_${skuType.toUpperCase()}_SUCCESS_BANNER_FLAG`
  }

  /**
   * @returns an array of objects with messageID and requestID
   * @description This function retrieves the message request IDs from local storage and validates them.
   * It ensures that the data is in the expected format and filters out any invalid entries.
   */
  private retrieveAndValidateRequestIDs(): MessageRequestID[] {
    const currentIDs = this.localStorage.getItem(this.COPILOT_MESSAGE_REQUEST_ID_KEY)
    try {
      const parsedValues = JSON.parse(currentIDs || '[]')
      if (!Array.isArray(parsedValues) || parsedValues.length === 0) {
        return []
      }
      const validIDs = parsedValues.filter((item: {messageID: string; requestID: string}) => {
        return typeof item.messageID === 'string' && typeof item.requestID === 'string'
      })
      return validIDs as MessageRequestID[]
    } catch {
      // if parsing fails, just return an empty array
      return []
    }
  }

  /**
   * Adds a requestID keyed on messageID to localstorage.
   * The tuple of (requestID, messageID) is stored in localstorage.
   * If the messageID already exists, it will be replaced with the new requestID.
   * If the number of stored requestIDs exceeds the maximum limit, the oldest one will be removed.
   * @param messageID messageID
   * @param requestID requestID -- Assigned by the server to each request
   */
  addRequestID(messageID: string, requestID: string): void {
    const currentIDs = this.retrieveAndValidateRequestIDs()
    // Check if the messageID already exists
    // This really should not happen because the tuple of (messageID, requestID) is unique
    // but if it does, we need to remove the old one
    const existingIndex = currentIDs.findIndex(item => item.messageID === messageID)
    if (existingIndex !== -1) {
      // If it exists, remove it from the array
      currentIDs.splice(existingIndex, 1)
    }
    currentIDs.push({messageID, requestID})
    if (currentIDs.length > this.MAX_MESSAGE_REQUEST_ID_STORAGE_COUNT) {
      currentIDs.shift()
    }
    this.localStorage.setItem(this.COPILOT_MESSAGE_REQUEST_ID_KEY, JSON.stringify(currentIDs))
  }

  /**
   * Retrieved the requestID for a given messageID if it exists
   * @param messageID messageID for which you want to get the requestID
   * @returns requestID or undefined
   */
  getRequestID(messageID: string): string | undefined {
    const currentIDs = this.retrieveAndValidateRequestIDs()
    const foundID = currentIDs.find(item => item.messageID === messageID)
    return foundID ? foundID.requestID : undefined
  }
}

export const copilotLocalStorage = new CopilotLocalStorage()

/** All methods of CopilotLocalStorage that take a single argument of type T */
type CopilotLocalStorageUnaryMethod<T> = {
  [K in keyof CopilotLocalStorage as CopilotLocalStorage[K] extends (arg: T) => unknown
    ? K
    : never]: CopilotLocalStorage[K]
}

/** All methods of CopilotLocalStorage that take no arguments */
type CopilotLocalStorageNullaryMethod<T> = {
  [K in keyof CopilotLocalStorage as CopilotLocalStorage[K] extends () => T ? K : never]: CopilotLocalStorage[K]
}

/**
 * A React hook that provides a way to get and set values in CopilotLocalStorage.
 * It takes a getter and setter method from CopilotLocalStorage and returns the current value and a setter function.
 * The setter function updates both the local state and the CopilotLocalStorage.
 */
export function useCopilotLocalStorage<T>(
  getter: keyof CopilotLocalStorageNullaryMethod<T>,
  setter: keyof CopilotLocalStorageUnaryMethod<T>,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => copilotLocalStorage[getter]() as T)
  const wrappedSetter = useCallback(
    (newValue: T) => {
      ;(copilotLocalStorage[setter] as (arg: T) => void)(newValue)
      setValue(newValue)
    },
    [setter],
  )
  return [value, wrappedSetter]
}
